import { requestContext } from 'client/utils/request-context/request-context';
import { isNode } from 'client/utils/environment';
// eslint-disable-next-line no-restricted-imports
import stringify from 'fast-safe-stringify';
import { getErrorProps, loggerMethods, loggerLevels } from 'client/utils/logger/logger-utils';

let venomLogEventHandler;
const EVENT_LISTENER_NAME = 'venom-log-event';

// returns request specific metadata
function getMetadata() {
  const loggerMetadata = { ...(requestContext.get('loggerMetadata') ?? {}) };

  loggerMetadata.isNode = isNode();

  /*
    how does this thing work?
    If there is an ctx.set() that happens elsewhere in venom,
    you can store a key/value in the http context which is a magical place, probably a global variable in memory.
    That key/value ctx can later be retrived here.
  */
  return loggerMetadata; // case where logging happens on app start
}

/**
 * Logger object that provides logging methods.
 *
 * This object is created using the methods from {@link loggerMethods}.
 *
 * @type {Object|null}
 */

export let logger = null;

function buildLogMessage(addMetadata, obj, msg, additionalAttributes) {
  const objString = typeof obj === 'string';
  const objError = obj instanceof Error;

  // build log message
  let message;
  if (objString) {
    // if obj is a string use it as a log message
    message = obj;
  } else {
    // if obj is error and msg is undefined, use error message as log message
    // otherwise use msg
    message = msg || (objError && obj.message) || undefined;
    // stringify message if it's not a string
    if (typeof message !== 'string') {
      message = stringify(message);
    }
  }

  // build log data
  // start with request metadata (reqId, url, ...)
  let data = { ...(addMetadata && getMetadata()), ...additionalAttributes };
  // if obj is not a string add it to the log data
  if (!objString && obj) {
    // if obj is an error use pino serializer to format Error data
    if (objError) {
      const { status } = getErrorProps(obj);
      const propsToCopyToTopLevel = status ? { status } : {}; // canary query looks for status at the top level
      data = {
        ...propsToCopyToTopLevel,
        err: obj,
        ...data, // add existing data (request metadata) last so it's not overwritten by Error data
      };
    } else {
      data = {
        ...obj,
        ...data, // add existing data (request metadata) last so it's not overwritten by Error data
      };
    }
  }

  return { message, data };
}

const isLogPossible = (configLevel, level) => configLevel <= level;
/**
 * @param config
 * @param outputLogger logger for using to output
 * @returns {*}
 */
export function initLogger(config, outputLogger) {
  if (logger) return logger;
  const { level: configLevel, addMetadata } = config;
  const configLevelValue = loggerLevels[configLevel];
  // creates log function for the specific log level
  function levelLogger(level) {
    return function log(obj, msg, additionalAttributes) {
      const levelValue = loggerLevels[level];
      if (!isLogPossible(configLevelValue, levelValue)) {
        return;
      }

      const { message, data } = buildLogMessage(
        addMetadata || level === loggerLevels.debug,
        obj,
        msg,
        additionalAttributes
      );

      // log message
      try {
        outputLogger[level](data, message);
      } catch (e) {
        console.error('Unexpected logger error: ', e); // eslint-disable-line no-console
      }
    };
  }

  // logger object
  logger = loggerMethods.reduce((acc, level) => {
    acc[level] = levelLogger(level);
    return acc;
  }, {});

  if (!isNode()) {
    venomLogEventHandler = event => {
      const { level, obj, message, additionalAttributes } = event.detail;

      logger[level]?.(obj, message, additionalAttributes);
    };

    // This listener subscribes to our custom EVENT_LISTENER_NAME event, acting as the
    // entry point for log requests from decoupled parts of the application.
    // For example, the script injected into our Webpack bundles uses this
    // event to send logs without needing a direct reference to this logger module.
    window.addEventListener(EVENT_LISTENER_NAME, venomLogEventHandler);
  }

  return logger;
}

// Function needed to clear a custom event listener. Used mainly in tests to prevent side effects.
export function cleanupLogger() {
  if (venomLogEventHandler) {
    window.removeEventListener(EVENT_LISTENER_NAME, venomLogEventHandler);

    venomLogEventHandler = null; // Clear the reference
  }
}
